/** Plain-words helpers for the admin console (#2833): counts and dates in words. */

const NUMBER_WORDS = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
] as const;

/** "Three" for 3, "40" past twelve. */
export function countWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/** "One person", "Three people", "No one yet". */
export function peopleWords(n: number): string {
  if (n <= 0) return "No one yet";
  return `${countWord(n)} ${n === 1 ? "person" : "people"}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parts(iso: string): { y: number; m: number; d: number } | null {
  // A timestamp reads in the viewer's local time. A plain date stays as it is.
  if (/^\d{4}-\d{2}-\d{2}T/.test(iso)) {
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() };
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}

/** "8 Nov", or "8 Nov 2025" when the year is not this year. */
export function dayWords(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const p = parts(iso);
  if (!p) return null;
  const year = p.y === now.getFullYear() ? "" : ` ${p.y}`;
  return `${p.d} ${MONTHS[p.m]}${year}`;
}

/** "2 to 15 Nov", "26 Oct to 8 Nov", or a year on each end when it is not this year. */
export function spanWords(
  start: string | null | undefined,
  end: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const a = start ? parts(start) : null;
  const b = end ? parts(end) : null;
  if (!a || !b) return null;
  const withYear = a.y !== b.y || a.y !== now.getFullYear();
  const one = (p: { y: number; m: number; d: number }, showMonth: boolean) =>
    `${p.d}${showMonth ? ` ${MONTHS[p.m]}` : ""}${withYear && showMonth ? ` ${p.y}` : ""}`;
  if (a.y === b.y && a.m === b.m && a.d === b.d) return one(b, true);
  const sameMonth = a.y === b.y && a.m === b.m;
  return `${one(a, !sameMonth)} to ${one(b, true)}`;
}

/** "2 Sep, 10:00" for a moment in time. */
export function momentWords(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear() === now.getFullYear() ? "" : ` ${d.getFullYear()}`;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}, ${hh}:${mm}`;
}

/** "Harbourline Security · Control room" becomes the organisation and the team. */
export function splitTeamName(name: string): { org: string | null; team: string } {
  const at = name.indexOf(" · ");
  if (at < 0) return { org: null, team: name };
  const org = name.slice(0, at).trim();
  const team = name.slice(at + 3).trim();
  // An empty part, or a second dot straight after the first, is no split.
  if (!org || !team || team.startsWith("·")) return { org: null, team: name };
  return { org, team };
}

interface PersonRef {
  email?: string | null;
  display_name?: string | null;
}

/** A person's name when we have one, else the email. */
export function personLabel(p: PersonRef): string {
  return p.display_name?.trim() || p.email || "";
}

/** The owner's name when the owner is also a lead with a name, else the owner's email. */
export function ownerLabel(row: {
  owner_account_id?: string | null;
  owner_email?: string | null;
  leads?: ReadonlyArray<PersonRef & { account_id: string }> | null;
}): string {
  const lead = (row.leads ?? []).find((l) => l.account_id === row.owner_account_id);
  return lead?.display_name?.trim() || row.owner_email || "";
}

/** Whole local calendar days from one instant to another, never below 0. */
export function calendarDaysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  // Round, because a day with a clock change is 23 or 25 hours long.
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000));
}

/** "just now", "5 min ago", "2 h ago", "yesterday", "3 days ago", then a date: "2 Sep". */
export function agoWords(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = calendarDaysBetween(then, now);
  if (days === 0) {
    const minutes = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 60_000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)} h ago`;
  }
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return dayWords(iso, now);
}
